import { useState } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { ColorPicker } from "../../../components/ColorPicker/ColorPicker.tsx";
import { Dialog } from "../../../components/Dialog/Dialog.tsx";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import type { RgbaColor } from "../../../types.ts";
import { hexToRgbaString, rgbaToHex } from "../../../utils/colors.ts";
import { useWorkspaceColors } from "../../hooks/useWorkspaceColors.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import { BrandDeletionButton } from "./components/BrandDeletionButton.tsx";
import { BrandKitSection } from "./components/BrandKitSection.tsx";

export const BrandColorSection = () => {
  const [color, setColor] = useState<RgbaColor>({
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  });
  const [isOpen, setIsOpen] = useState(false);
  const workspaceUuid = useWorkspaceUuid();

  const brandColors = useWorkspaceColors();

  const { mutate: createBrandColor, isLoading: isCreatingBrandColor } =
    useAppMutation({
      path: `workspaces/${workspaceUuid}/brand-colors`,
      invalidate: [`workspaces/${workspaceUuid}`],
    }).mutation;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        content={
          <div className="flex-col-center px-200 pb-200 gap-100">
            <ColorPicker initialColor={color} onChange={setColor} />
            <div className="flex-row items-center gap-200 w-full justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  createBrandColor({ color: rgbaToHex(color) });
                  setIsOpen(false);
                }}
                disabled={isCreatingBrandColor}
                loading={isCreatingBrandColor}
              >
                Add color
              </Button>
            </div>
          </div>
        }
        title="Add brand color"
      />
      <BrandKitSection
        title="Colors"
        onAdd={() => {
          setIsOpen(true);
        }}
      >
        {brandColors.length === 0 ? (
          <div className="flex-col-center gap-400">
            <Icon name="Palette" size="xl" />
            <div className="body-md-default text-secondary">
              Define your brand colors to be able to use them from anywhere in
              the app.
            </div>
          </div>
        ) : (
          <div className="flex-row items-center gap-400">
            {brandColors.map((brandColor, index) => (
              <div
                key={index}
                className="relative group flex-col-center w-2000 gap-200"
              >
                <div
                  className="w-full aspect-square rounded-full border"
                  style={{
                    backgroundColor: hexToRgbaString(brandColor.color),
                  }}
                />
                <div className="label-md-semibold text-secondary">
                  {brandColor.color.toUpperCase()}
                </div>
                <div className="absolute right-0 top-0 invisible group-hover:visible">
                  <BrandDeletionButton brandUuid={brandColor.uuid} />
                </div>
              </div>
            ))}
          </div>
        )}
      </BrandKitSection>
    </>
  );
};
